// SinCos3D shader adapted from Shadertoy-style raymarching snippet

const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_seed;

// Camera and scene params
uniform float u_cam_z;            // camera z position (negative pulls back)
uniform float u_line_thickness;   // lt
uniform float u_point_thickness;  // pt
uniform float u_yaw_speed;        // auto yaw speed
uniform float u_pitch;            // pitch in radians
uniform float u_yaw_offset;       // base yaw offset in radians

// Helper to build rotation matrix from angle 'a'
#define A(v) mat2(cos(m.v + radians(vec4(0.0, -90.0, 90.0, 0.0))))
// Signed distances
#define W(v) length(vec3(p.yz - v(p.x + vec2(0.0, pi_2) + t), 0.0)) - lt
#define P(v) length(p - vec3(0.0, v(t), v(t + pi_2))) - pt

void main() {
  vec2 R = u_resolution.xy;
  vec2 U = gl_FragCoord.xy;

  float lt = u_line_thickness;
  float pt = u_point_thickness;
  float pi = 3.14159265359;
  float pi2 = pi * 2.0;
  float pi_2 = pi * 0.5;
  float t = u_time * u_speed * pi;

  // Auto-rotate when not interacting
  vec2 m = vec2(u_yaw_offset - t * u_yaw_speed, u_pitch);

  vec3 o = vec3(0.0, 0.0, u_cam_z); // cam
  vec3 u = normalize(vec3((U - 0.5 * R) / R.y, 1.0));
  vec3 c = vec3(0.0), k = c, p;

  mat2 v = A(y), h = A(x); // pitch & yaw

  float d = 0.0;
  float s = 1.0;

  // raymarch
  for (int i = 0; i < 80; i++) {
    p = o + u * d;
    p.yz *= v;
    p.xz *= h;
    p.x -= 3.0; // slide objects to the right a bit
    if (p.y < -1.5) p.y = 2.0 / p.y; // reflect into neg y
    k.x = min( max(p.x + lt, W(sin)), P(sin) ); // sine wave
    k.y = min( max(p.x + lt, W(cos)), P(cos) ); // cosine wave
    s = min(s, min(k.x, k.y)); // blend
    if (s < 0.001 || d > 100.0) break; // limits
    d += s * 0.5;
  }

  // add and color scene
  vec3 col = max(cos(d * pi2) - s * sqrt(max(d, 0.0)) - k, 0.0);
  col.gb += 0.1;
  gl_FragColor = vec4(col * 0.4 + col.brg * 0.6 + col * col, 1.0);
}
`;

const shader = {
  name: 'SinCos3D',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'u_cam_z',
      type: 'number',
      label: 'Camera Z',
      min: -20,
      max: -1,
      step: 0.1,
      default: -7,
    },
    {
      key: 'u_line_thickness',
      type: 'number',
      label: 'Line Thickness',
      min: 0.01,
      max: 0.5,
      step: 0.005,
      default: 0.1,
    },
    {
      key: 'u_point_thickness',
      type: 'number',
      label: 'Point Thickness',
      min: 0.05,
      max: 1.0,
      step: 0.01,
      default: 0.3,
    },
    {
      key: 'u_yaw_speed',
      type: 'number',
      label: 'Yaw Speed',
      min: 0.0,
      max: 1.0,
      step: 0.001,
      default: 0.05,
    },
    {
      key: 'u_pitch',
      type: 'number',
      label: 'Pitch (rad)',
      min: -3.1416,
      max: 3.1416,
      step: 0.001,
      default: 0,
    },
    {
      key: 'u_yaw_offset',
      type: 'number',
      label: 'Yaw Offset (rad)',
      min: -6.2832,
      max: 6.2832,
      step: 0.001,
      default: -1.5708,
    },
    { key: 'u_seed', type: 'number', label: 'Seed', min: 0, max: 9999, step: 1, default: 0 },
    {
      key: 'u_speed',
      type: 'number',
      label: 'Global Speed',
      min: 0,
      max: 10,
      step: 0.1,
      default: 1,
    },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(SinCos3D, u_cam_z=-7; u_line_thickness=0.1; u_point_thickness=0.3; animated=true)',
  ],
};

export default shader;
